/**
 * Universal Platform Detection Service
 * 
 * Detects whether the app is running as:
 *   - WEB: Regular browser or PWA (uses Stripe)
 *   - ANDROID: Google Play TWA or Android WebView (uses Google Play Billing)
 *   - IOS: iOS WKWebView wrapper or iOS standalone PWA (uses Apple StoreKit)
 * 
 * Works regardless of the wrapping technology (native Xcode/Android Studio,
 * Capacitor, PWABuilder, etc.).
 */

// Platform constants
export const PLATFORM = {
  WEB: 'web',
  ANDROID: 'android',
  IOS: 'ios',
};

// Payment method constants
export const PAYMENT_METHOD = {
  STRIPE: 'stripe',
  GOOGLE_PLAY: 'google_play',
  APPLE_IAP: 'apple_iap',
};

let _detectedPlatform = null;
let _detectedPaymentMethod = null;

/**
 * Detect if running inside an iOS native wrapper.
 * Primary detection: Despia runtime via user-agent string.
 * Fallback: WKWebView message handlers, generic bridges.
 */
function isIOSNativeWrapper() {
  const ua = navigator.userAgent.toLowerCase();

  // Method 1: Despia runtime (recommended — user-agent contains 'despia')
  if (ua.includes('despia') && (ua.includes('iphone') || ua.includes('ipad'))) return true;

  // Method 2: WKWebView message handlers injected by native app
  if (window.webkit?.messageHandlers?.storeKit) return true;

  // Method 3: Generic native bridge injected by wrapper
  if (window.EiliNativeBridge?.platform === 'ios') return true;
  if (window.NativePaymentBridge?.platform === 'ios') return true;

  // Method 4: Custom flag set by native app via JS injection
  if (window.__EILI_PLATFORM__ === 'ios') return true;

  return false;
}

/**
 * Detect if running inside an Android native wrapper.
 *
 * Strategy stack (most-specific → most-generic):
 *   1. Despia runtime UA marker
 *   2. TWA via Digital Goods API
 *   3. Android-app referrer
 *   4. Custom JS bridges injected by the wrapper
 *   5. Custom platform flag
 *   6. Android WebView UA marker (`; wv)` substring) — matches stock
 *      Android WebView used by Android Studio-built apps. Standard
 *      Chrome on Android does NOT include `wv)` so this is safe.
 *      Reference: https://developer.chrome.com/docs/multidevice/user-agent#webview_user_agent
 */
function isAndroidNativeWrapper() {
  const ua = navigator.userAgent.toLowerCase();

  // Method 1: Despia runtime on Android
  if (ua.includes('despia') && ua.includes('android')) return true;

  // Method 2: Digital Goods API (TWA from Play Store)
  if ('getDigitalGoodsService' in window) return true;

  // Method 3: Android app referrer
  if (document.referrer.startsWith('android-app://')) return true;

  // Method 4: Generic native bridge injected by wrapper
  if (window.EiliNativeBridge?.platform === 'android') return true;
  if (window.NativePaymentBridge?.platform === 'android') return true;

  // Method 5: Custom flag set by native app via JS injection
  if (window.__EILI_PLATFORM__ === 'android') return true;

  // Method 6: Android WebView UA marker. Apps built in Android Studio
  // that embed a standard `WebView` produce a UA with `; wv)` and
  // `Version/<n>` — distinct from regular Chrome on Android. Without
  // this, plain Android Studio-built apps were detected as desktop
  // browser → wrong payment method, no OneSignal external-id link,
  // and the install banner appeared on top of a native app.
  if (ua.includes('android') && ua.includes('; wv)')) return true;

  return false;
}

/**
 * Best-effort device-model extraction from the userAgent string.
 *
 * Android UAs typically embed the device model in the form
 *   `(Linux; Android <ver>; <Model>; ...)` — examples:
 *     `(Linux; Android 14; Pixel 7)`
 *     `(Linux; Android 13; SM-G990U; wv)`
 *     `(Linux; Android 12; Pixel 6 Pro)`
 *
 * Returns null on any browser whose UA doesn't include a recognizable
 * Android model (iOS, desktop, etc.).
 */
export function getAndroidDeviceModel() {
  const ua = navigator.userAgent || '';
  const m = ua.match(/Android\s+[\d._]+;\s*([^;)]+)/i);
  if (!m) return null;
  // Strip a trailing "Build/..." segment if present (older Android UAs).
  return m[1].replace(/\s+Build\/.*/i, '').trim();
}

/**
 * Detect if running as standalone PWA on iOS (Home Screen app).
 * Note: iOS standalone PWAs cannot use Apple IAP — they fall back to Stripe.
 */
function isIOSStandalonePWA() {
  return window.navigator.standalone === true;
}

/**
 * Get the detected platform.
 * Caches result after first detection.
 */
export function getPlatform() {
  if (_detectedPlatform) return _detectedPlatform;

  if (isIOSNativeWrapper()) {
    _detectedPlatform = PLATFORM.IOS;
  } else if (isAndroidNativeWrapper()) {
    _detectedPlatform = PLATFORM.ANDROID;
  } else {
    _detectedPlatform = PLATFORM.WEB;
  }

  return _detectedPlatform;
}

/**
 * Get the appropriate payment method for the current platform.
 * 
 * - iOS native wrapper → Apple IAP (required by App Store)
 * - Android TWA/WebView → Google Play Billing (required by Play Store)
 * - Web/PWA → Stripe
 * - iOS standalone PWA → Stripe (no IAP available outside native wrapper)
 */
export function getPaymentMethod() {
  if (_detectedPaymentMethod) return _detectedPaymentMethod;

  const platform = getPlatform();

  if (platform === PLATFORM.IOS) {
    _detectedPaymentMethod = PAYMENT_METHOD.APPLE_IAP;
  } else if (platform === PLATFORM.ANDROID) {
    _detectedPaymentMethod = PAYMENT_METHOD.GOOGLE_PLAY;
  } else {
    _detectedPaymentMethod = PAYMENT_METHOD.STRIPE;
  }

  return _detectedPaymentMethod;
}

/**
 * Check if native payment capabilities are available.
 * Returns true if the native bridge is properly connected and ready.
 */
export async function isNativePaymentReady() {
  const method = getPaymentMethod();

  if (method === PAYMENT_METHOD.GOOGLE_PLAY) {
    // Despia Android: always ready inside the runtime
    if (navigator.userAgent.toLowerCase().includes('despia')) return true;
    try {
      if ('getDigitalGoodsService' in window) {
        const service = await window.getDigitalGoodsService('https://play.google.com/billing');
        return !!service;
      }
    } catch {
      return false;
    }
    return false;
  }

  if (method === PAYMENT_METHOD.APPLE_IAP) {
    // Despia iOS: always ready inside the runtime
    if (navigator.userAgent.toLowerCase().includes('despia')) return true;
    return !!(
      window.webkit?.messageHandlers?.storeKit ||
      window.EiliNativeBridge?.purchase ||
      window.NativePaymentBridge?.purchase
    );
  }

  // Stripe is always "ready" (handled by web checkout)
  return true;
}

/**
 * Get a human-readable summary of the detected platform and payment method.
 * Useful for debugging and support.
 */
export function getPlatformInfo() {
  const ua = navigator.userAgent.toLowerCase();
  const isAndroidWebView = ua.includes('android') && ua.includes('; wv)');
  return {
    platform: getPlatform(),
    paymentMethod: getPaymentMethod(),
    userAgent: navigator.userAgent,
    androidDeviceModel: getAndroidDeviceModel(),
    isDespia: ua.includes('despia'),
    isDespiaIOS: ua.includes('despia') && (ua.includes('iphone') || ua.includes('ipad')),
    isDespiaAndroid: ua.includes('despia') && ua.includes('android'),
    isAndroidWebView,
    isStandalone: window.matchMedia('(display-mode: standalone)').matches,
    hasDigitalGoodsAPI: 'getDigitalGoodsService' in window,
    hasWKWebView: !!window.webkit?.messageHandlers,
    hasNativeBridge: !!(window.EiliNativeBridge || window.NativePaymentBridge),
    iosStandalone: window.navigator.standalone === true,
  };
}

/**
 * Reset detection cache (useful for testing).
 */
export function resetDetection() {
  _detectedPlatform = null;
  _detectedPaymentMethod = null;
}
